import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SweetAlert2 from "react-sweetalert2";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ApiStatusTypeEnum } from "../../../enumeration/ApiStatusTypeEnum";
import { useAuth } from "../../auth/components/AuthInit";
import * as actions from "../redux/DashboardAction";
import { CompanyBalanceModal } from "./CompanyBalanceModal";
import "./dashboard.css";
import { UnblockUserModal } from "./UnblockUserModal";

const rangeSelection = {
  0: "all",
  7: "weekly",
  1: "daily",
};

export default function Dashboard() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [swalProps, setSwalProps] = useState({});
  const [show, setShow] = useState(false);
  const [showUnblock, setShowUnblock] = useState(false);
  const dispatch = useDispatch();
  const dashboardState = useSelector(
    ({ dashboard }) => dashboard,
    shallowEqual
  );
  const { auth } = useAuth();
  useEffect(() => {
    handleSearchClick();
  }, [dispatch]);

  const handleSearchClick = () => {
    const queryParams = {
      action: "dashboardV2",
    };

    dispatch(actions.checkDashboardList(queryParams)).then((resp) => {
      resp = resp.data;
      if (parseInt(resp.code) === ApiStatusTypeEnum.COMMON_ERROR) {
        setSwalProps({
          show: true,
          icon: "error",
          title: intl.formatMessage({ id: "error" }),
          text: resp.message,
        });
      }
    });
  };

  return (
    <div>
      {dashboardState.actionsLoading && (
        <div
          className="position-fixed w-100 top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center"
          style={{
            scale: "1.5",
            background: "rgba(0,0,0,0.3)",
            zIndex: "99999",
          }}
        >
          <div className="spinner-border" role="status">
            <span className="sr-only"></span>
          </div>
        </div>
      )}
      <SweetAlert2
        onResolve={() => {
          setSwalProps({});
        }}
        backdrop={false}
        {...swalProps}
        customClass="custom-swal"
      />
      <CompanyBalanceModal
        setShow={setShow}
        show={show}
        setSwalProps={setSwalProps}
      />
      <UnblockUserModal
        setShow={setShowUnblock}
        show={showUnblock}
        setSwalProps={setSwalProps}
      />
      <div>
        <h2>{intl.formatMessage({ id: "dashboard" })}</h2>
      </div>
      <div className="mt-4">
        <div>
          {intl.formatMessage({ id: "dateRange" })} :{" "}
          {dashboardState.dashboardList?.daterange ?? "Loading..."}
        </div>
      </div>
      <div className="row">
        <div className="col-md-3 p-2">
          <div className="dashboard-container rounded border shadow-lg p-2">
            <div className="mb-2">
              {intl.formatMessage({ id: "registerCount" })}
            </div>
            <div className="fw-bold">
              {dashboardState.dashboardList?.totalregister ?? "Loading..."}
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-3 p-2">
          <div className="dashboard-container rounded border shadow-lg p-2">
            <div className="mb-2">{intl.formatMessage({ id: "topup" })}</div>
            <div className="fw-bold">
              {dashboardState.dashboardList?.totaltopupamount ?? "Loading..."}
            </div>
          </div>
        </div>
        <div className="col-md-3 p-2">
          <div className="dashboard-container rounded border shadow-lg p-2">
            <div className="mb-2">{intl.formatMessage({ id: "withdraw" })}</div>
            <div className="fw-bold">
              {dashboardState.dashboardList?.totalwithdrawamount ??
                "Loading..."}
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 p-2">
          <div className="dashboard-container rounded border shadow-lg p-2">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                layout="vertical"
                data={dashboardState.dashboardList?.companyNetGrow
                  .map((item) => ({
                    ...item,
                    topup: parseFloat(item.topup),
                    withdraw: parseFloat(item.withdraw),
                  }))
                  .slice(-0)}
                margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="dateday" type="category" />
                <Tooltip
                  contentStyle={{ backgroundColor: "#888888" }}
                  itemStyle={{ color: "#ffffff" }}
                  labelStyle={{ color: "#000000" }}
                />
                <Legend />
                <Bar
                  dataKey="topup"
                  fill="#008000"
                  name={intl.formatMessage({ id: "topup" })}
                />
                <Bar
                  dataKey="withdraw"
                  fill="#FF0000"
                  name={intl.formatMessage({ id: "withdraw" })}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="text-center">
              {intl.formatMessage({ id: "netGrow" })}
            </div>
          </div>
        </div>
        <div className="col-md-6 p-2">
          <div className="dashboard-container rounded border shadow-lg p-2">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={dashboardState.dashboardList?.companyMemberGrow
                  .map((item) => ({
                    ...item,
                    registercount: parseInt(item.registercount, 10),
                    activeuser: parseInt(item.activeuser, 10),
                  }))
                  .slice(-0)}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dateday" />
                <YAxis />
                <Tooltip
                  contentStyle={{ backgroundColor: "#888888" }}
                  itemStyle={{ color: "#ffffff" }}
                  labelStyle={{ color: "#000000" }}
                />
                <Bar
                  dataKey="registercount"
                  fill="#8884d8"
                  name={intl.formatMessage({ id: "registerCount" })}
                />
                <Bar
                  dataKey="activeuser"
                  fill="#ffff00"
                  name={intl.formatMessage({ id: "activeUser" })}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="text-center">
              {intl.formatMessage({ id: "memberGrow" })}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="dashboard-container rounded border shadow-lg p-2">
            <button
              className="btn btn-primary w-100"
              onClick={() => setShow(true)}
            >
              {intl.formatMessage({ id: "companyBalance" })}
            </button>
          </div>
        </div>
        {(auth.selmasterid ?? auth.masterid)?.length === 3 && (
          <div className="col-md-4">
            <div className="dashboard-container rounded border shadow-lg p-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => setShowUnblock(true)}
              >
                {intl.formatMessage({ id: "unblockAccount" })}
              </button>
            </div>
          </div>
        )}
        {(auth.selmasterid ?? auth.masterid) === "JST" && (
          <div className="col-md-4">
            <div className="dashboard-container rounded border shadow-lg p-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => navigate("companyGame")}
              >
                {intl.formatMessage({ id: "companyGame" })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
